import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminEditRequest, AdminEditRequestList, UpdateEditRequest } from '@mys/contracts';
import { api } from './api.js';

/* ============================================================================
   The change queue's queries.
   ----------------------------------------------------------------------------
   These talk to `/api/admin/edit-requests`, which is a different path from the
   portal's `/api/portal/edit-requests`. That one is scoped to the logged-in
   client; this one is scoped to nobody, on purpose. Do not "tidy" them into
   one client.
   ========================================================================== */

export type EditRequestStatus = 'open' | 'done';

export const EDIT_REQUEST_STATUS_LABELS: Record<EditRequestStatus, string> = {
  open: 'Open',
  done: 'Done',
};

export function useEditRequests(filters: { status?: EditRequestStatus; q?: string }, limit = 50) {
  return useQuery({
    queryKey: ['edit-requests', filters, limit],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.q) params.set('q', filters.q);
      params.set('limit', String(limit));
      return api.get<AdminEditRequestList>(`/api/admin/edit-requests?${params.toString()}`, signal);
    },
  });
}

export function useUpdateEditRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string } & UpdateEditRequest) =>
      api.patch<{ ok: true; editRequest: AdminEditRequest }>(`/api/admin/edit-requests/${id}`, {
        status,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['edit-requests'] }),
  });
}

/**
 * How long a request has been waiting, in whole days.
 *
 * Pure, so it is testable without a component, and computed from `createdAt`
 * rather than stored. Only `open` requests are worth ageing: a finished one
 * having taken eleven days is history, not a queue signal.
 */
export function daysWaiting(createdAt: string, now = new Date()): number {
  const start = new Date(createdAt).getTime();
  return Math.max(0, Math.floor((now.getTime() - start) / 86_400_000));
}

/**
 * A prefilled WhatsApp reply, because replying is what closes one of these.
 *
 * The client's own words are quoted back so they can see which request is
 * being answered — they may have sent three.
 */
export function replyLink(request: Pick<AdminEditRequest, 'phone' | 'message'>): string {
  const quoted =
    request.message.length > 120 ? `${request.message.slice(0, 117)}…` : request.message;
  const text = encodeURIComponent(`Namaste! Aapka change — "${quoted}" — dekh liya hai.`);
  return `https://wa.me/${request.phone.replace(/^\+/, '')}?text=${text}`;
}
